import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { isBefore, parseISO } from 'date-fns';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import type { EspState } from '@/store/esp/state.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { AccumulatorScheduleLineResponse } from '@/store/forwardAccumulator/api/tradeCapture/response.model.ts';
import type {
  ForwardAccumulatorQuote,
  ForwardAccumulatorRfsStatus,
  FxForwardAccumulator,
  TcKey,
} from '@/store/forwardAccumulator/state.ts';
import type { AppState } from '@/store/store.ts';
import { createWeakAppSelector as createAppSelector } from '@/store/storeMethods.ts';
import type { TcValidation } from '@/store/types.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectForwardAccumulator = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId] as FxForwardAccumulator | undefined,
  (forwardAccumulator) => forwardAccumulator,
);

export const selectForwardAccumulatorSettlementFrequencyData = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.settlementFrequency,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.numberOfSettlements,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.settlementFrequencyList,
  (settlementFrequency, numberOfSettlements, settlementFrequencyList) => ({
    settlementFrequency,
    numberOfSettlements,
    settlementFrequencyList,
  }),
);

export const selectForwardAccumulatorTradeCaptureError = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => (accumulator?.tradeCapture?.status === 'ERROR' ? accumulator.tradeCapture.error : undefined),
);

export const selectForwardAccumulatorCrossCurrencyData = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.isCrossed,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.crossCurrency,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.crossCurrenciesList,
  (isCrossed, crossCurrency, crossCurrenciesList) => ({
    isCrossed,
    crossCurrency,
    crossCurrenciesList,
  }),
);

export const selectForwardAccumulatorFixingReference1Data = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.fixingReference1,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.fixingReference1Default,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.fixingReference1List,
  (fixingReference1, fixingReference1Default, fixingReference1List) => ({
    fixingReference1,
    fixingReference1Default,
    fixingReference1List,
  }),
);

export const selectForwardAccumulatorFixingReference2Data = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.fixingReference2,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.fixingReference2Default,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.fixingReference2List,
  (fixingReference2, fixingReference2Default, fixingReference2List) => ({
    fixingReference2,
    fixingReference2Default,
    fixingReference2List,
  }),
);

export const selectForwardAccumulatorCustomFields = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => accumulator?.properties.customFields,
);

export const selectForwardAccumulatorScheduleData = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.schedule,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.scheduleCustomFields,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.scheduleIds,
  (schedule, scheduleCustomFields, scheduleIds) => ({
    schedule,
    scheduleCustomFields,
    scheduleIds,
  }),
);

export const selectForwardAccumulatorFirstFixing = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.firstFixingDate,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.firstFixingTenor,
  (firstFixingDate, firstFixingTenor) => ({
    firstFixingDate,
    firstFixingTenor: isNotDefined(firstFixingTenor) ? undefined : firstFixingTenor,
  }),
);

export const selectForwardAccumulatorLastFixing = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.lastFixingDate,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.lastFixingTenor,
  (lastFixingDate, lastFixingTenor) => ({
    lastFixingDate,
    lastFixingTenor: isNotDefined(lastFixingTenor) ? undefined : lastFixingTenor,
  }),
);

export const selectForwardAccumulatorCurrencyData = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.currencyPair,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.notionalCurrencyIndex,
  (currencyPair, notionalCurrencyIndex) => {
    if (isNotDefined(currencyPair)) {
      return {
        notionalCurrencyIndex,
        currency: undefined,
      };
    }

    const [currency1, currency2] = currencyPair.split('/');
    const currency = notionalCurrencyIndex === 1 ? currency1 : currency2;

    return {
      notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectForwardAccumulatorProperty: <T extends keyof FxForwardAccumulator['properties']>(
  state: AppState,
  tileId: string,
  property: T,
) => FxForwardAccumulator['properties'][T] | undefined = createAppSelector(
  <T>(state: AppState, tileId: string, _property: T) => selectForwardAccumulator(state, tileId),
  <T>(_state: AppState, _tileId: string, property: T) => property,
  <T extends keyof FxForwardAccumulator['properties']>(product: FxForwardAccumulator | undefined, property: T) =>
    product?.properties[property],
);

export const selectForwardAccumulatorMarkupCurrency = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => accumulator?.properties.markupCurrency,
);

export const selectForwardAccumulatorErrors = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => (accumulator?.tradeCapture.status === 'CHECKED' ? accumulator.tradeCapture.errors : undefined),
);
export const selectForwardAccumulatorWarnings = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => (accumulator?.tradeCapture.status === 'CHECKED' ? accumulator.tradeCapture.warnings : undefined),
);

export const selectForwardAccumulatorValidation = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => (accumulator?.tradeCapture.status === 'CHECKED' ? accumulator.tradeCapture.validation : undefined),
);

export const selectForwardAccumulatorPremiumDateData = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.premiumDate,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.premiumDateTenor,
  (premiumDate, premiumDateTenor) => ({
    premiumDate: premiumDate ?? undefined,
    premiumDateTenor: premiumDateTenor ?? undefined,
  }),
);

export const selectForwardAccumulatorOrderedScheduleItemIds = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.scheduleIds,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.schedule,
  (scheduleIds, schedule) => {
    if ((scheduleIds?.length ?? 0) === 0 || isNotDefined(schedule)) {
      return [];
    }
    const getOrderableItems = Object.fromEntries((scheduleIds ?? []).map((id) => [id, schedule[id]?.fixingDate]));

    return Object.keys(getOrderableItems).sort(
      (id1, id2) => getOrderableItems[id1]?.localeCompare(getOrderableItems[id2] ?? '') ?? 1,
    );
  },
);

export const selectForwardAccumulatorScheduleFieldValidation = createAppSelector(
  (state: AppState, tileId: string, _legId: string, _faultyValue: keyof TcKey<AccumulatorScheduleLineResponse>) =>
    selectForwardAccumulatorValidation(state, tileId),
  (_state: AppState, _tileId: string, legId: string) => legId,
  (_state: AppState, _tileId: string, _legId: string, faultyValue: keyof TcKey<AccumulatorScheduleLineResponse>) =>
    faultyValue,
  (validations, legId, faultyValue): { validation: TcValidation[] } => {
    const scheduleValidation = validations?.schedule?.[legId];

    const validation = scheduleValidation?.[faultyValue];
    return { validation: isDefined(validation) ? [validation] : [] };
  },
);

export const selectForwardAccumulatorScheduleTable = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.schedule,
  (_state: AppState, _tileId: string, legId: string) => legId,
  (schedule, legId) => schedule?.[legId],
);

export const selectForwardAccumulatorHedgeData = createAppSelector(
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.hedgeCurrencyIndex,
  (state: AppState, tileId: string) => state.forwardAccumulator[tileId]?.properties.hedgeAmount,
  (hedgeCurrencyIndex, hedgeAmount) => ({
    hedgeCurrencyIndex: hedgeCurrencyIndex ?? undefined,
    hedgeAmount: hedgeAmount ?? undefined,
  }),
);

interface CurrencyPairStuff {
  currencyPair?: string;
  currencyPairPrecision: number;
}

export const selectCurrencyPairInfo = createAppSelector(
  [(state: AppState) => state, (_state, tileId: string) => tileId],
  (state, tileId): CurrencyPairStuff => {
    const currencyPair = state.forwardAccumulator[tileId]?.properties.currencyPair ?? undefined;

    return {
      currencyPair,
      currencyPairPrecision: selectCurrencyPairPrecision(state, currencyPair),
    };
  },
);

function selectEsp(state: AppState): EspState {
  return state.esp;
}

function selectTiering(state: AppState, tileId: string): string | undefined {
  const tile = state.forwardAccumulator[tileId];
  return tile?.properties.tiering;
}

export const selectForwardAccumulatorEspSpot = createAppSelector(
  [selectCurrencyPairInfo, selectEsp, selectTiering],
  ({ currencyPair }, esp, tiering) => {
    if (isNotDefined(currencyPair) || isNotDefined(tiering)) {
      return undefined;
    }
    const espStreamId = getEspStreamId(currencyPair, tiering);
    const espPrice = esp.streams[espStreamId];

    if (isNotDefined(espPrice) || espPrice.status !== 'PRICED' || espPrice.priceType !== 'ESP.TRADER.PRICE') {
      return undefined;
    }

    const precision = espPrice.currencyPairPrecision;

    return convertPointsToRate(espPrice.midRate, precision);
  },
);

//██████╗ ███████╗███████╗
//██╔══██╗██╔════╝██╔════╝
//██████╔╝█████╗  ███████╗
//██╔══██╗██╔══╝  ╚════██║
//██║  ██║██║     ███████║
//╚═╝  ╚═╝╚═╝     ╚══════╝

export function isPriceable(accumulator: FxForwardAccumulator): boolean {
  return accumulator.tradeCapture.status === 'CHECKED' && accumulator.tradeCapture.isReadyToPrice;
}

export function getForwardAccumulatorQuote(accumulator: FxForwardAccumulator) {
  if (accumulator?.properties.rfs?.status === 'PRICING' || accumulator?.properties.rfs?.status === 'EXPIRED') {
    return accumulator.properties.rfs.quote;
  }
}

export function selectForwardAccumulatorRfsSpotHedge(accumulator: FxForwardAccumulator) {
  if (accumulator?.properties.rfs?.status === 'PRICING' || accumulator?.properties.rfs?.status === 'EXPIRED') {
    return accumulator.properties.rfs.quote.hedge?.rateMid;
  }
}

export function selectForwardAccumulatorRfsHedgeAmount(accumulator: FxForwardAccumulator) {
  if (accumulator?.properties.rfs?.status === 'PRICING' || accumulator?.properties.rfs?.status === 'EXPIRED') {
    return accumulator.properties.rfs.quote.hedge?.notionalAmount;
  }
}

export function getForwardAccumulatorMarginData(accumulator: FxForwardAccumulator) {
  if (accumulator?.properties.rfs?.status === 'PRICING' || accumulator?.properties.rfs?.status === 'EXPIRED') {
    return accumulator.properties.rfs.quote.marginData;
  }
}

export function getForwardAccumulatorMarketData(accumulator: FxForwardAccumulator) {
  if (accumulator?.properties.rfs?.status === 'PRICING' || accumulator?.properties.rfs?.status === 'EXPIRED') {
    const quote = accumulator.properties.rfs.quote;
    return {
      forward: quote.forward,
      swapPoints: quote.swapPoints,
      volatility: quote.volatility,
      delta: [quote.greeks.deltaCcy1, quote.greeks.deltaCcy2],
      vega: [quote.greeks.vegaCcy1, quote.greeks.vegaCcy2],
      premiumRateStoCcyMid: [quote.premiumData?.premiumRateStoCcy1Mid, quote.premiumData?.premiumRateStoCcy2Mid],
    };
  }
}

export function getForwardAccumulatorRfsId(accumulator: FxForwardAccumulator): string | undefined {
  if (
    accumulator.properties.rfs.status === 'PRICING' ||
    accumulator.properties.rfs.status === 'AWAITING' ||
    accumulator.properties.rfs.status === 'ERROR'
  ) {
    return accumulator.properties.rfs.rfsId;
  }
}

function isRfsStatusNotExecutable(forwardAccumulator: FxForwardAccumulator, isPriceButton?: boolean): boolean {
  return isPriceButton ? isPremiumAmountNegative(forwardAccumulator) : false;
}

export function selectForwardAccumulatorRfsStatus(
  accumulator: FxForwardAccumulator,
  isPriceButton = false,
): ForwardAccumulatorRfsStatus {
  if (!areAllExpiryDatesValid(accumulator)) {
    return 'NOT_PRICEABLE';
  }

  switch (accumulator.properties.rfs.status) {
    case 'ERROR': {
      const isBypassable = accumulator.properties.rfs.error.multipassErrorCode === 'RrCreditCheckFailed'; // && sl.isUserInternalSales(state);
      return isBypassable ? 'BYPASSABLE_ERROR' : 'ERROR';
    }

    case 'CANCELLED':
    case 'EXPIRED':
      return isPriceable(accumulator) ? 'PRICEABLE' : 'NOT_PRICEABLE';

    case 'AWAITING':
      return accumulator.properties.rfs.quoteType === 'Tradeable' ? 'AWAITING_EXECUTABLE' : 'AWAITING_INDICATIVE';

    case 'PRICING': {
      if (isRfsStatusNotExecutable(accumulator, isPriceButton)) {
        return 'NOT_PRICEABLE';
      }

      return accumulator.properties.rfs.quoteType === 'Tradeable' ? 'STREAMING_EXECUTABLE' : 'STREAMING_INDICATIVE';
    }

    default:
      assertUnreachable(accumulator.properties.rfs, 'Unhandled stream status');
  }
}

export function areAllExpiryDatesValid(forwardAccumulator: FxForwardAccumulator) {
  const maxDate = forwardAccumulator.properties.maxDate;
  if (isNotDefined(maxDate)) {
    return true;
  }

  const expiry = forwardAccumulator.properties.expiryDate;
  return isNotDefined(expiry) || isBefore(parseISO(expiry), maxDate);
}

export function getForwardAccumulatorQuoteType(
  forwardAccumulator: FxForwardAccumulator,
): 'Indicative' | 'Tradeable' | 'Withdraw' {
  if (
    forwardAccumulator.properties.rfs.status === 'PRICING' ||
    forwardAccumulator.properties.rfs.status === 'AWAITING'
  ) {
    return forwardAccumulator.properties.rfs.quoteType;
  }
  return 'Indicative';
}

export function isPremiumAmountNegative(forwardAccumulator: FxForwardAccumulator): boolean {
  const premiumData = getForwardAccumulatorRfsPremiumData(
    forwardAccumulator.properties.rfs.status === 'PRICING' ? forwardAccumulator.properties.rfs.quote : undefined,
  );
  if (!premiumData?.premiumAmount) {
    return false;
  }

  const premiumAmountData = premiumData.premiumAmount[forwardAccumulator.properties.premiumTypeCurrencyIndex - 1];
  return (premiumAmountData ?? 0) > 0 || !forwardAccumulator.properties.isPriceable;
}

export interface ForwardAccumulatorRfsPremiumData {
  premiumAmount: readonly [number, number];
  premiumPercent: readonly [number, number];
  premiumRateSto: readonly [number, number];
}

export function getForwardAccumulatorRfsPremiumData(
  quote: ForwardAccumulatorQuote | undefined,
): ForwardAccumulatorRfsPremiumData | undefined {
  if (isNotDefined(quote?.premiumData)) {
    return undefined;
  }

  return {
    premiumAmount: [quote.premiumData.premiumCcy1.ask, quote.premiumData.premiumCcy2.ask] as const,
    premiumPercent: [quote.premiumData.premiumPctCcy1, quote.premiumData.premiumPctCcy2] as const,
    premiumRateSto: [quote.premiumData.premiumRateStoCcy1Mid, quote.premiumData.premiumRateStoCcy2Mid] as const,
  };
}

export function getForwardAccumulatorRfsQuoteId(forwardAccumulator: FxForwardAccumulator): string | undefined {
  if (isNotDefined(forwardAccumulator.properties.rfs)) {
    return undefined;
  }

  return forwardAccumulator.properties.rfs.status === 'PRICING' ? forwardAccumulator.properties.rfs.quoteId : undefined;
}

export function getForwardAccumulatorTraderId(option: FxForwardAccumulator): string | undefined {
  if (option.properties.rfs.status === 'AWAITING' || option.properties.rfs.status === 'PRICING') {
    return option.properties.rfs.traderId;
  }
}

export function getForwardAccumulatorContribution(forwardAccumulator: FxForwardAccumulator): 'Automatic' | 'Manual' {
  if (
    forwardAccumulator?.properties.rfs.status === 'PRICING' ||
    forwardAccumulator?.properties.rfs.status === 'EXPIRED'
  ) {
    return forwardAccumulator.properties.rfs.quote.quoteContribution;
  }
  return 'Manual';
}

export const selectDisplayMode = createAppSelector(
  (state: AppState, tileId: string) => selectForwardAccumulator(state, tileId),
  (accumulator) => accumulator?.properties.uiState.displayMode ?? 'Pricer',
);
